import { omit } from 'lodash'
import { APIClient } from 'infra/APIClient'
import { APIConfig, CursorPaged, CursorPageQuery } from '../types/definitions'
import { WorkspaceDomainDTO } from './dtos/domain'
import {
  AcceptInvitePayload,
  CreateInvitationPayload,
  Invitation,
} from './dtos/invitations'
import {
  AddManySkillsToMemberPayload,
  CreateMemberManuallyPayload,
  CreateMemberPayload,
  FindMembersQuery,
  GetManyMembersByPublicIdPayload,
  GetManyMembersPayload,
  GetMemberByPublicIdPayload,
  GetMemberPayload,
  MemberDTO,
  RemoveManySkillsToMemberPayload,
  UpdateMemberPayload,
} from './dtos/member'
import { PermissionDescriptionDTO } from './dtos/permissions'
import {
  CreateRolePayload,
  FindRolesQuery,
  RoleDTO,
  UpdateRolePayload,
} from './dtos/role'
import {
  CreateSkillPayload,
  FindSkillsQuery,
  SkillDTO,
  UpdateSkillPayload,
} from './dtos/skill'
import {
  CreateTeamParams,
  FindTeamQuery,
  FindUserTeamsQuery,
  GetManyTeamParams,
  GetTeamParams,
  TeamDTO,
  UpdateTeamParams,
} from './dtos/team'
import {
  CreateTeamMemberPayload,
  FindTeamMembersQuery,
  GetManyTeamMembersParam,
  GetPendingMembersForTeamParams,
  GetTeamMemberParams,
  GetTeamMembersForUserParams,
  GetTeamMembersForWorkspaceByUserPublicId,
  GetTeamMembersForWorkspaceMemberParams,
  PendingTeamMemberDTO,
  TeamMemberDTO,
  UpdateTeamMemberPayload,
} from './dtos/team-member'
import {
  CreateTeamRolePayload,
  FindTeamRolesQuery,
  TeamRoleDTO,
  UpdateTeamRolePayload,
} from './dtos/team-role'
import { UserDTO, UserIdPairMap } from './dtos/user'
import {
  CreateWorkspacePayload,
  DeleteWorkspacePayload,
  FindWorkspacesQuery,
  MembershipDTO,
  UpdateWorkspacePayload,
  WorkspaceDTO,
} from './dtos/workspace'
import { rethrowRequestError } from 'utils/error-util'
import { TranslationDTO } from './dtos/translation'
import { Id } from 'types/definitions'
import Translation from './models/Translation'
import { UpdateUserDataPayload, UserDataDTO } from './dtos/user-data'
import { AuthUserDTO } from './dtos/auth-user'
import { CancellationToken } from '../infra/cancellation'
import {
  TermsAcceptanceAcceptDTO,
  TermsAcceptanceCheckDTO,
  TermsOfUseGetDTO,
} from './dtos/terms-of-use'
import { EnrollMFAResponse } from './dtos/mfa'
import { ProviderDTO, UpdateProviderUserDataPayload } from './dtos/provider'
import { SearchableUserDTO } from './dtos/searchable-user'
import { FetchUsersQuery, GetUserParams } from './SearchableUserStore'
import { MagicLinkDTO, MagicLinkPayload } from './dtos/magic-link'
import { AccountMergeRequestFilterOption } from '../admin/account-linking/AccountMergeRequestSearchViewModel'
import { AccountMergeRequestDto } from '../admin/account-linking/AccountMergeRequest'
import {
  CreateCustomerPayload,
  CreateCustomerResponseDTO,
} from './dtos/customer'

export interface IAMAPI {
  domains: DomainAPI
  invitations: InvitationAPI
  members: MemberAPI
  permissions: PermissionAPI
  skills: SkillsAPI
  teams: TeamAPI
  teamMembers: TeamMemberAPI
  teamRoles: TeamRoleAPI
  translations: TranslationsAPI
  roles: RolesAPI
  users: UserAPI
  auth: AuthAPI
  workspaces: WorkspaceAPI
  userData: UserDataAPI
  userSearch: UserSearchAPI
  termsOfUse: TermsAPI
  providers: ProviderAPI
  impersonation: ImpersonationAPI
  magicLink: MagicLinkAPI
  accountMerging: MergeRequestsAPI
  customers: CustomerAPI
  platformAdmin: PlatformAdminAPI
}

export function createIAMAPI(opts: APIConfig): IAMAPI {
  return {
    domains: new DomainAPI(opts),
    invitations: new InvitationAPI(opts),
    members: new MemberAPI(opts),
    permissions: new PermissionAPI(opts),
    skills: new SkillsAPI(opts),
    teams: new TeamAPI(opts),
    teamMembers: new TeamMemberAPI(opts),
    teamRoles: new TeamRoleAPI(opts),
    translations: new TranslationsAPI(opts),
    roles: new RolesAPI(opts),
    users: new UserAPI(opts),
    auth: new AuthAPI(opts),
    workspaces: new WorkspaceAPI(opts),
    userData: new UserDataAPI(opts),
    termsOfUse: new TermsAPI(opts),
    providers: new ProviderAPI(opts),
    userSearch: new UserSearchAPI(opts),
    impersonation: new ImpersonationAPI(opts),
    magicLink: new MagicLinkAPI(opts),
    accountMerging: new MergeRequestsAPI(opts),
    customers: new CustomerAPI(opts),
    platformAdmin: new PlatformAdminAPI(opts),
  }
}

export class CustomerAPI extends APIClient {
  create(payload: CreateCustomerPayload): Promise<CreateCustomerResponseDTO> {
    return this.client
      .post(`/v3/workspaces/${payload.workspace_id}/customers`, payload)
      .then(this._data)
  }
}

export class DomainAPI extends APIClient {
  get(idOrDomain: string): Promise<WorkspaceDomainDTO> {
    return this.client.get(`/v3/domains/${idOrDomain}`).then(this._data)
  }
}

export class InvitationAPI extends APIClient {
  get(id: string): Promise<Invitation> {
    return this.client.get(`/v3/invitations/${id}`).then(this._data)
  }

  create(payload: CreateInvitationPayload): Promise<Invitation> {
    return this.client.post('/v3/invitations', payload).then(this._data)
  }

  accept({ token, ...payload }: AcceptInvitePayload): Promise<MemberDTO> {
    return this.client
      .post(`/v3/invitations/${token}`, payload)
      .then(this._data)
  }
}

export class MemberAPI extends APIClient {
  find(
    workspaceId: string,
    params: FindMembersQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<MemberDTO>> {
    return this.client
      .get(`/v3/workspaces/${workspaceId}/members`, {
        params,
        signal: cancellationToken.asAbortSignal(),
      })
      .then(this._data)
  }

  /**
   * Member search. `has_permission` matches the platform role merged with the workspace role.
   */
  search(
    workspaceId: string,
    params: FindMembersQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<MemberDTO>> {
    return this.client
      .get(`/v3/workspaces/${workspaceId}/members/search`, {
        params,
        signal: cancellationToken.asAbortSignal(),
      })
      .then(this._data)
  }

  get(params: GetMemberPayload): Promise<MemberDTO> {
    return this.client
      .get(`/v3/workspaces/${params.workspace_id}/members/${params.user_id}`, {
        params,
      })
      .then(this._data)
  }

  addRole(
    params: GetMemberByPublicIdPayload,
    roleId: string,
  ): Promise<MemberDTO> {
    return this.client
      .post(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/roles`,
        { role_id: roleId, user_id: params.user_public_id },
      )
      .then(this._data)
  }

  removeRole(
    params: GetMemberByPublicIdPayload,
    roleId: string,
  ): Promise<MemberDTO> {
    return this.client
      .delete(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/roles/${roleId}`,
      )
      .then(this._data)
  }

  addSkill(
    params: GetMemberByPublicIdPayload,
    skillId: string,
  ): Promise<MemberDTO> {
    return this.client
      .post(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/skills`,
        { skill_id: skillId, user_id: params.user_public_id },
      )
      .then(this._data)
  }

  removeSkill(
    params: GetMemberByPublicIdPayload,
    skillId: string,
  ): Promise<MemberDTO> {
    return this.client
      .delete(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/skills/${skillId}`,
      )
      .then(this._data)
  }

  addManySkillsByPublicId(
    params: AddManySkillsToMemberPayload,
  ): Promise<MemberDTO> {
    return this.client
      .post(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/skills/batch`,
        { skill_ids: params.skill_ids },
      )
      .then(this._data)
  }

  removeManySkillsByPublicId(
    params: RemoveManySkillsToMemberPayload,
  ): Promise<MemberDTO> {
    return this.client
      .post(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/skills/batch/delete`,
        { skill_ids: params.skill_ids },
      )
      .then(this._data)
  }

  getMany(payload: GetManyMembersPayload): Promise<Array<MemberDTO | null>> {
    return this.client
      .post(
        '/v3/members/id-query',
        payload.map((param) =>
          param.user_public_id
            ? omit(param, 'user_id')
            : omit(param, 'user_public_id'),
        ),
      )
      .then(this._data)
  }

  getManyByPublicId(
    payload: GetManyMembersByPublicIdPayload,
  ): Promise<Array<MemberDTO | null>> {
    return this.client.post('/v3/members/id-query', payload).then(this._data)
  }

  create(data: CreateMemberPayload): Promise<MemberDTO> {
    return this.client.post('/v3/members', data).then(this._data)
  }

  createManually(data: CreateMemberManuallyPayload): Promise<MemberDTO> {
    return this.client
      .post('/v3/members/create-manually', data)
      .then(this._data)
  }

  update(
    params: GetMemberByPublicIdPayload,
    data: UpdateMemberPayload,
  ): Promise<MemberDTO> {
    return this.client
      .patch(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}`,
        data,
      )
      .then(this._data)
  }

  updateLastLogin(workspaceId: string): Promise<void> {
    return this.client
      .patch(`/v3/workspaces/${workspaceId}/login`)
      .then(this._data)
  }

  updateLastSeen(workspaceId: string): Promise<void> {
    return this.client
      .patch(`/v3/workspaces/${workspaceId}/ping`)
      .then(this._data)
  }

  changeAvatar(
    workspaceId: string,
    userPublicId: string,
    file: File,
  ): Promise<MemberDTO | null> {
    const formData = new window.FormData()
    formData.append('file', file, file.name)
    return this.client
      .put(
        `/v3.1/workspaces/${workspaceId}/members/${userPublicId}/avatar`,
        formData,
      )
      .then(this._data)
  }
}

export class PermissionAPI extends APIClient {
  find(): Promise<Array<PermissionDescriptionDTO>> {
    return this.client.get('/v3/permissions').then(this._data)
  }
}

export class TeamAPI extends APIClient {
  find(params: FindTeamQuery): Promise<CursorPaged<TeamDTO>> {
    return this.client
      .get(`/v3/workspaces/${params.workspace_id}/teams`, {
        params,
      })
      .then(this._data)
  }

  get(params: GetTeamParams): Promise<TeamDTO> {
    if (params.workspace_id) {
      return this.client
        .get(`/v3/workspaces/${params.workspace_id}/teams/${params.id}`, {
          params,
        })
        .then(this._data)
    }
    return this.client
      .get(`/v3/teams/${params.id}`, { params })
      .then(this._data)
  }

  getMany(params: GetManyTeamParams): Promise<Array<TeamDTO | null>> {
    return this.client.post('/v3/teams/id-query', params).then(this._data)
  }

  create(data: CreateTeamParams): Promise<TeamDTO> {
    return this.client.post('/v3/teams', data).then(this._data)
  }

  update(data: UpdateTeamParams, teamId: string): Promise<TeamDTO> {
    return this.client.patch(`/v3/teams/${teamId}`, data).then(this._data)
  }

  changeAvatar(teamId: string, file: File): Promise<TeamDTO | null> {
    const formData = new window.FormData()
    formData.append('file', file, file.name)
    return this.client
      .put(`/v3/teams/${teamId}/avatar`, formData)
      .then(this._data)
  }
}

export class TeamMemberAPI extends APIClient {
  find(params: FindTeamMembersQuery): Promise<CursorPaged<TeamMemberDTO>> {
    return this.client
      .get(`/v3/teams/${params.team_id}/team-members`, {
        params,
      })
      .then(this._data)
  }

  get(params: GetTeamMemberParams): Promise<TeamMemberDTO> {
    return this.client
      .get(`/v3/teams/${params.team_id}/team-members/${params.user_id}`, {
        params,
      })
      .then(this._data)
  }

  getMany(
    params: GetManyTeamMembersParam,
  ): Promise<Array<TeamMemberDTO | null>> {
    return this.client
      .post('/v3.1/team-members/id-query', params)
      .then(this._data)
  }

  getTeamMembersForWorkspaceMember(
    params: GetTeamMembersForWorkspaceMemberParams,
  ): Promise<Array<TeamMemberDTO>> {
    return this.client
      .get(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_id}/team-members`,
        { params },
      )
      .then(this._data)
  }

  getTeamMembersForWorkspaceMembersByUserPublicId(
    params: GetTeamMembersForWorkspaceByUserPublicId,
    cancellationToken = CancellationToken.default,
  ): Promise<Array<TeamMemberDTO>> {
    return this.client
      .get(
        `/v3.1/workspaces/${params.workspace_id}/members/${params.user_public_id}/team-members`,
        {
          params,
          signal: cancellationToken.asAbortSignal(),
        },
      )
      .then(this._data)
  }

  getTeamMembersForUser(
    params: GetTeamMembersForUserParams,
  ): Promise<Array<TeamMemberDTO>> {
    return this.client
      .get(`/v3.1/team-members/${params.user_id}`, { params })
      .then(this._data)
  }

  getPendingMembersForTeam(
    params: GetPendingMembersForTeamParams,
  ): Promise<CursorPaged<PendingTeamMemberDTO>> {
    return this.client
      .get(`/v3/invitationsPending/`, { params })
      .then(this._data)
  }

  create(payload: CreateTeamMemberPayload): Promise<TeamMemberDTO> {
    return this.client.post('/v3/team-members', payload).then(this._data)
  }

  createByPublicId(payload: CreateTeamMemberPayload): Promise<TeamMemberDTO> {
    return this.client.post('/v3.1/team-members', payload).then(this._data)
  }

  update(
    params: GetTeamMemberParams,
    payload: UpdateTeamMemberPayload,
  ): Promise<TeamMemberDTO> {
    return this.client
      .patch(
        `/v3.1/teams/${params.team_id}/team-members/${params.user_id}`,
        payload,
      )
      .then(this._data)
  }

  delete(params: GetTeamMemberParams): Promise<void> {
    return this.client
      .delete(`/v3.1/teams/${params.team_id}/team-members/${params.user_id}`, {
        params,
      })
      .then(this._data)
  }
}

export class TeamRoleAPI extends APIClient {
  find(params: FindTeamRolesQuery): Promise<CursorPaged<TeamRoleDTO>> {
    return this.client.get('/v3/team-roles', { params })
  }

  get(roleId: string): Promise<TeamRoleDTO> {
    return this.client.get(`/v3/team-roles/${roleId}`).then(this._data)
  }

  create(payload: CreateTeamRolePayload): Promise<TeamRoleDTO> {
    return this.client.post('/v3/team-roles', payload).then(this._data)
  }

  update(payload: UpdateTeamRolePayload): Promise<TeamRoleDTO> {
    return this.client
      .post(`/v3/team-roles/${payload.id}`, payload)
      .then(this._data)
  }

  delete(roleId: string): Promise<void> {
    return this.client.delete(`/v3/team-roles/${roleId}`).then(this._data)
  }
}

export class TranslationsAPI extends APIClient {
  getAll(workspaceId: string): Promise<Translation[]> {
    return this.client
      .get<TranslationDTO[]>(`/v3/translations/${workspaceId}`)
      .then(({ data }) =>
        data.map((translation) => new Translation(translation)),
      )
  }

  find(workspaceIds: readonly Id[]): Promise<Translation[]> {
    return this.client
      .get<TranslationDTO[]>(`/v3/translations/find`, {
        params: { workspaceIds },
      })
      .then(({ data }) =>
        data.map((translation) => new Translation(translation)),
      )
  }
}

export class SkillsAPI extends APIClient {
  get(id: string): Promise<SkillDTO> {
    return this.client.get(`/v3/skills/${id}`).then(this._data)
  }

  find(params: FindSkillsQuery): Promise<CursorPaged<SkillDTO>> {
    return this.client
      .get(`/v3/workspaces/${params.workspace_id}/skills`, { params })
      .then(this._data)
  }

  create(data: CreateSkillPayload): Promise<SkillDTO> {
    return this.client.post('/v3/skills', data).then(this._data)
  }

  update(id: string, data: UpdateSkillPayload): Promise<SkillDTO> {
    return this.client.post(`/v3/skills/${id}`, data).then(this._data)
  }

  delete(id: string): Promise<void> {
    return this.client.delete(`/v3/skills/${id}`).then(this._data)
  }
}

export class RolesAPI extends APIClient {
  get(id: string): Promise<RoleDTO> {
    return this.client.get(`/v3/roles/${id}`).then(this._data)
  }

  find(params: FindRolesQuery): Promise<Array<RoleDTO>> {
    return this.client
      .get(`/v3/workspaces/${params.workspace_id}/roles`, { params })
      .then(this._data)
  }

  create(data: CreateRolePayload): Promise<RoleDTO> {
    return this.client.post('/v3/roles', data).then(this._data)
  }

  update(id: string, data: UpdateRolePayload): Promise<RoleDTO> {
    return this.client.post(`/v3/roles/${id}`, data).then(this._data)
  }

  delete(id: string): Promise<void> {
    return this.client.delete(`/v3/roles/${id}`).then(this._data)
  }
}

export class AuthAPI extends APIClient {
  authenticate(token: string): Promise<AuthUserDTO> {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }

    return this.client.post('/v3/auth', {}, config).then(this._data)
  }

  enrollMFA(): Promise<EnrollMFAResponse> {
    return this.client.post(`/v3/auth/enroll-mfa`).then(this._data)
  }

  fetchUser(token: string, publicId: string): Promise<UserDTO> {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }

    return this.client.get(`/v3.1/users/${publicId}`, config).then(this._data)
  }
}

export class UserAPI extends APIClient {
  getUserWorkspaceMemberships<T>(
    userId: string,
    query?: T,
  ): Promise<MembershipDTO[]> {
    return this.client
      .get(`/v3.1/users/${userId}/workspace-memberships`, {
        params: query,
      })
      .then(this._data)
  }

  get(publicId: string): Promise<UserDTO> {
    return this.client.get(`/v3.1/users/${publicId}`).then(this._data)
  }

  getMany(ids: readonly string[]): Promise<Array<UserDTO | null>> {
    return this.client.post(`/v3/users/id-query`, ids).then(this._data)
  }

  getManyByPublicId(ids: readonly string[]): Promise<Array<UserDTO | null>> {
    return this.client.post(`/v3.1/users/id-query`, ids).then(this._data)
  }

  getResetPasswordRequestById(id: string) {
    return this.client
      .get(`/v3/users/reset-password-request/${id}`)
      .then(this._data)
  }

  resetPassword(id: string, password: string) {
    return this.client
      .patch(`/v3/users/reset-password-request/${id}`, { password })
      .then(this._data)
  }

  logout(): Promise<void> {
    return this.client.post('/v3/users/logout')
  }

  lookupPrivateIds(ids: string[]): Promise<UserIdPairMap> {
    return this.client
      .post(`/v3/internal-ops/user-private-id`, ids)
      .then(this._data)
  }

  lookupPublicIds(ids: string[]): Promise<UserIdPairMap> {
    return this.client
      .post(`/v3/internal-ops/user-public-id`, ids)
      .then(this._data)
  }

  getTeams(params: FindUserTeamsQuery): Promise<CursorPaged<TeamDTO>> {
    return this.client
      .get(`/v3/users/${params.userPublicId}/teams`, { params })
      .then(this._data)
  }
}

export class ProviderAPI extends APIClient {
  get(publicId: string): Promise<ProviderDTO> {
    return this.client.get(`/v3/providers/${publicId}`).then(this._data)
  }

  update(
    userPublicId: string,
    payload: UpdateProviderUserDataPayload,
  ): Promise<void> {
    return this.client
      .patch(`/v3/providers/${userPublicId}/user-data`, payload)
      .then(this._data)
  }
}

export class UserSearchAPI extends APIClient {
  get(publicId: string, params: GetUserParams): Promise<SearchableUserDTO> {
    return this.client
      .post(`/v3/user-search/${publicId}`, params)
      .then(this._data)
  }

  find(params: FetchUsersQuery): Promise<CursorPaged<SearchableUserDTO>> {
    return this.client
      .get(`/v3/user-search/search`, { params })
      .then(this._data)
  }
}

export class WorkspaceAPI extends APIClient {
  get(id: string): Promise<WorkspaceDTO> {
    return this.client
      .get(`/v3/workspaces/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getMany(ids: readonly string[]): Promise<Array<WorkspaceDTO | null>> {
    return this.client
      .post(
        '/v3/workspaces/id-query',
        ids.map((id) => ({ id })),
      )
      .then(this._data)
  }

  find(params: FindWorkspacesQuery): Promise<CursorPaged<WorkspaceDTO>> {
    return this.client.get('/v3/workspaces', { params }).then(this._data)
  }

  create(data: CreateWorkspacePayload): Promise<WorkspaceDTO> {
    return this.client.post('/v3/workspaces', data).then(this._data)
  }

  update(id: string, data: UpdateWorkspacePayload): Promise<WorkspaceDTO> {
    return this.client.patch(`/v3/workspaces/${id}`, data).then(this._data)
  }

  delete(id: string, data: DeleteWorkspacePayload): Promise<void> {
    return this.client
      .delete(`/v3/workspaces/${id}`, { params: data.force_archive })
      .then(this._data)
  }
}

export class UserDataAPI extends APIClient {
  async getByPublicId(userPublicId: Id): Promise<UserDataDTO> {
    return this.client
      .get(`/v3/users/${userPublicId}/user-data`)
      .then(this._data)
  }

  async update(
    userPublicId: Id,
    payload: UpdateUserDataPayload,
  ): Promise<void> {
    this.client
      .put(`/v3/users/${userPublicId}/user-data`, payload)
      .then(this._data)
  }
}

export class TermsAPI extends APIClient {
  getLatestForWorkspace(workspaceId: string): Promise<TermsOfUseGetDTO> {
    return this.client.get(`/v3/terms-of-use/${workspaceId}`).then(this._data)
  }

  checkAcceptanceForWorkspace(
    workspaceId: string,
  ): Promise<TermsAcceptanceCheckDTO> {
    return this.client
      .post('/v3/terms-acceptance', { workspace_id: workspaceId })
      .then(this._data)
  }

  acceptLatestForWorkspace(
    workspaceId: string,
  ): Promise<TermsAcceptanceAcceptDTO> {
    return this.client
      .post('/v3/terms-acceptance/accept', {
        workspace_id: workspaceId,
      })
      .then(this._data)
  }

  getLatestForPlatform(): Promise<TermsOfUseGetDTO> {
    return this.client.get('/v3/terms-of-use').then(this._data)
  }

  checkAcceptanceForPlatform(): Promise<TermsAcceptanceCheckDTO> {
    return this.client.post('/v3/terms-acceptance').then(this._data)
  }

  acceptLatestForPlatform(): Promise<TermsAcceptanceAcceptDTO> {
    return this.client.post('/v3/terms-acceptance/accept').then(this._data)
  }
}

export class ImpersonationAPI extends APIClient {
  async impersonate(
    userPublicId: string,
    workspaceId: string,
    callback: string,
  ): Promise<{ url: string }> {
    return this.client
      .post('/v3/impersonation', {
        user_id: userPublicId,
        workspace_id: workspaceId,
        callback,
      })
      .then(this._data)
  }
}

export class MagicLinkAPI extends APIClient {
  async generate(payload: MagicLinkPayload): Promise<MagicLinkDTO> {
    return this.client.post('/v4/magic-link', payload).then(this._data)
  }
}

export class MergeRequestsAPI extends APIClient {
  async list(
    query: CursorPageQuery,
    filter: AccountMergeRequestFilterOption,
  ): Promise<CursorPaged<AccountMergeRequestDto>> {
    return this.client
      .get('/v1/manual-merge-requests', {
        params: {
          ...this.toListFilter(filter),
          ...query,
        },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async link(id: string) {
    return this.client
      .post(`/v1/manual-merge-requests/${id}/link`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async reject(id: string) {
    return this.client
      .post(`/v1/manual-merge-requests/${id}/reject`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async ignore(id: string) {
    return this.client
      .post(`/v1/manual-merge-requests/${id}/ignore`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  private toListFilter(filter: AccountMergeRequestFilterOption) {
    return {
      filter:
        filter === AccountMergeRequestFilterOption.Processed
          ? 'processed'
          : 'pending',
    }
  }
}

interface JoinWorkspaceAsAdminResponse {
  user_public_id: string
  workspace_id: string
}

export class PlatformAdminAPI extends APIClient {
  joinWorkspaceAsAdmin(
    workspaceId: string,
  ): Promise<JoinWorkspaceAsAdminResponse> {
    return this.client
      .post(`/v3/platform-admin/workspaces/${workspaceId}/join`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  promoteToOwner(workspaceId: string): Promise<MemberDTO> {
    return this.client
      .post(`/v3/platform-admin/workspaces/${workspaceId}/promote-to-owner`)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}
